import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { PROJECTS } from "../../lib/projects";

/**
 * Featured Projects / Case Studies — honest showcase content.
 * NO fake metrics. Each card links to /projects/:slug for the full detail page.
 */
export const FeaturedProjects = () => {
  const { locale } = useI18n();
  const isDE = locale === "de";

  return (
    <section
      data-testid="featured-projects"
      className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-brand-orange" />
              <span className="label-mono">Featured Projects</span>
            </div>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-[56px] tracking-tight text-white leading-[1.05]">
              {isDE ? "Praxisbeispiele unserer Arbeit." : "Practical examples of our work."}
            </h2>
            <p className="mt-5 text-white/65 text-base md:text-lg leading-relaxed">
              {isDE
                ? "Showcase-Inhalte, die die Tiefe unserer Capabilities zeigen. Vertrauliche Kundennamen werden auf Anfrage und mit NDA geteilt."
                : "Showcase content that demonstrates the depth of our capabilities. Confidential client names are shared on request under NDA."}
            </p>
          </div>
          <Link to="/portfolio" className="btn-secondary" data-testid="projects-view-all">
            {isDE ? "Alle Projekte" : "View all"}
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROJECTS.map((p, i) => (
            <Link
              key={p.slug}
              to={`/projects/${p.slug}`}
              data-testid={`project-${i}`}
              className="glass rounded-3xl overflow-hidden flex flex-col hover:border-brand-orange/40 transition-all group hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(255,106,0,0.18)] duration-300"
            >
              <div className="relative h-52 md:h-56 overflow-hidden">
                <img
                  src={p.assets.hero}
                  alt={p.title[locale]}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-brand-orange/90 text-white text-[10px] font-mono uppercase tracking-[0.22em] shadow-lg">
                  {p.industry[locale]}
                </div>
                <div className="absolute bottom-3 right-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/75">
                  Case study
                </div>
              </div>
              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <h3 className="font-display font-semibold text-lg md:text-xl text-white tracking-tight group-hover:text-brand-orange transition-colors leading-snug">
                  {p.title[locale]}
                </h3>
                <p className="mt-3 text-sm text-white/65 line-clamp-3 flex-1 leading-relaxed">
                  {p.summary[locale]}
                </p>
                <div className="mt-6 pt-5 border-t border-white/[0.06] flex items-center justify-between">
                  <span className="label-mono text-brand-orange/90">
                    {isDE ? "Case Study ansehen" : "View case study"}
                  </span>
                  <span className="w-9 h-9 rounded-full bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center text-brand-orange group-hover:bg-brand-orange group-hover:text-white transition-all">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
